'use client';

import { ReactNode } from 'react';

import { FilterGroup } from '@/components/FilterGroup';
import { ProgramEligibilityFilterGroup } from '@/components/FilterGroup/ProgramEligibility';
import { ProgramEligibilityFilterGroupV2 } from '@/components/FilterGroup/ProgramEligibilityV2';
import { FacetsAccordion } from '@/components/FilterGroup/views';
import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { getKeyValue } from '@/services/content/data';
import { SearchResultGRS } from '@/services/search/services/GRS.types';
import { isSearchResultGRS } from '@/services/search/services/Grs';
import { isSearchResultLW } from '@/services/search/services/LucidWorks';
import { ResultsAnnouncer } from '@/src/containers/main/ResultsAnnouncer';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { GRSFacetConfigProperties } from '@/src/types/contentStack';
import { PageType } from '@/types/pages/search';
import { Box } from '@mui/material';
import { Divider } from '@nxt-costco-com/forge-components';
import type {
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';

import {
    A11Y_FILTER_DESCRIBED_BY,
    A11Y_FILTER_KEY,
    GRS_BOPIM_FACET_KEY,
    GRS_BOPIW_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_DELIVERY_FACET_KEY,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
    LW_BOPIM_FACET_KEY,
    LW_BOPIW_FACET_KEY,
    LW_DELIVERY_FACET_KEY,
    LW_SHOW_OUT_OF_STOCK_FACET_KEY,
} from './constants';
import { normalizeGRSFacets } from './grs.lib';
import { FilterLoadingView } from './loading.view';
import type { FacetNormalized } from './types';
import { remapFacetDisplayOrder, sortFacets } from './utils';

/**
 * WWarehouseSelectorComponent & DeliveryLocationSelectorComponent must be rendered
 * server side, so they need to be passed in as props to this component.
 */
export default function SearchResultsFacetsClient({
    categoryUrlPath,
    facetConfig,
    grsFacetConfig,
    WarehouseSelectorComponent,
    DeliveryLocationSelectorComponent,
    locale,
    pageType,
    categoryId,
    publishedCategories,
}: {
    categoryUrlPath: string | undefined;
    facetConfig: ContentStackEntryDataProps | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    WarehouseSelectorComponent: ReactNode;
    DeliveryLocationSelectorComponent: ReactNode;
    locale: LocaleProps;
    pageType: PageType;
    categoryId?: string;
    publishedCategories: string[] | undefined;
}) {
    const { searchResult, isSearchResultsLoading, searchService } =
        useSearchContext();

    if (isSearchResultsLoading) {
        return <FilterLoadingView />;
    }

    if (
        !searchResult ||
        !facetConfig ||
        (searchService === SEARCH_SERVICE_PROVIDERS.GRS && !grsFacetConfig)
    ) {
        console.error(
            'SearchResultsFacetsClient: Missing required props for rendering Search Facets.',
        );
        return null;
    }

    const searchResultLW =
        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
        isSearchResultLW(searchResult) &&
        searchResult;
    const searchResultGRS = (searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
        isSearchResultGRS(searchResult) &&
        searchResult) as SearchResultGRS | null;

    const normalizedLWFacets = searchResultLW && searchResultLW.facets;

    const normalizedGRSFacets =
        searchResultGRS &&
        grsFacetConfig &&
        normalizeGRSFacets(
            searchResultGRS?.searchResult.facets,
            grsFacetConfig,
            searchResultGRS?.isAdTargetingExplicitlyDisabled,
            locale,
        );

    return (
        <>
            <Box id={A11Y_FILTER_DESCRIBED_BY} sx={{ display: 'none' }}>
                {getKeyValue(A11Y_FILTER_KEY, facetConfig)}
            </Box>
            <ResultsAnnouncer
                announcerText={getKeyValue('a11y.newresults', facetConfig)}
            />
            {normalizedLWFacets &&
                normalizedLWFacets
                    ?.map(remapFacetDisplayOrder)
                    .sort(sortFacets)
                    .map((facet: FacetNormalized) => {
                        switch (facet.facetKey?.toLowerCase()) {
                            // Hide Unavailable Items
                            case LW_SHOW_OUT_OF_STOCK_FACET_KEY:
                                return (
                                    <Box key={facet.facetKey}>
                                        <Divider />
                                        <FilterGroup
                                            facet={facet}
                                            facetConfig={facetConfig}
                                            grsFacetConfig={grsFacetConfig}
                                            locale={locale}
                                            pageType={pageType}
                                            searchService={searchService}
                                        />
                                    </Box>
                                );
                            // Program Eligibility
                            case LW_DELIVERY_FACET_KEY:
                                return (
                                    <ProgramEligibilityFilterGroup
                                        key={facet.facetKey}
                                        facet={facet}
                                        facetConfig={facetConfig}
                                        WarehouseSelectorComponent={
                                            WarehouseSelectorComponent
                                        }
                                        DeliveryLocationSelectorComponent={
                                            DeliveryLocationSelectorComponent
                                        }
                                        locale={locale}
                                        pageType={pageType}
                                        bopimFacet={
                                            searchResultLW.facets?.filter(
                                                facet =>
                                                    facet.facetKey ===
                                                    LW_BOPIM_FACET_KEY,
                                            )[0] as FacetNormalized
                                        }
                                        bopiwFacet={
                                            searchResultLW.facets?.filter(
                                                facet =>
                                                    facet.facetKey ===
                                                    LW_BOPIW_FACET_KEY,
                                            )[0] as FacetNormalized
                                        }
                                        searchService={searchService}
                                    />
                                );

                            case LW_BOPIW_FACET_KEY:
                            case LW_BOPIM_FACET_KEY:
                                return null;

                            default:
                                // Even if the buckets array is empty, if it is returned we will pass it on.
                                // This is used in such cases like the Category filter list where we inject breadcrumbs
                                // into the filter even though the array comes back empty
                                if (facet.buckets) {
                                    return (
                                        <FacetsAccordion
                                            categoryUrlPath={categoryUrlPath}
                                            key={facet.facetKey}
                                            facet={facet}
                                            facetConfig={facetConfig}
                                            grsFacetConfig={grsFacetConfig}
                                            locale={locale}
                                            pageType={pageType}
                                            breadcrumb={
                                                searchResultLW.breadcrumb
                                            }
                                            searchService={searchService}
                                            categoryId={categoryId}
                                            publishedCategories={
                                                publishedCategories
                                            }
                                        />
                                    );
                                }
                                return null;
                        }
                    })}

            {normalizedGRSFacets &&
                normalizedGRSFacets
                    ?.map(remapFacetDisplayOrder)
                    .sort(sortFacets)
                    .map((facet: FacetNormalized) => {
                        switch (facet.facetKey.toLowerCase()) {
                            // Hide Unavailable Items
                            case GRS_SHOW_OUT_OF_STOCK_FACET_KEY:
                                return (
                                    <Box key={facet.facetKey}>
                                        <Divider />
                                        <FilterGroup
                                            facet={facet}
                                            facetConfig={facetConfig}
                                            locale={locale}
                                            pageType={pageType}
                                            searchService={searchService}
                                        />
                                    </Box>
                                );
                            // Program Eligibility
                            case GRS_DELIVERY_FACET_KEY:
                                return (
                                    <ProgramEligibilityFilterGroupV2
                                        DeliveryLocationSelectorComponent={
                                            DeliveryLocationSelectorComponent
                                        }
                                        WarehouseSelectorComponent={
                                            WarehouseSelectorComponent
                                        }
                                        bopimFacet={
                                            normalizedGRSFacets?.filter(
                                                (facet: FacetNormalized) =>
                                                    facet.facetKey ===
                                                    GRS_BOPIM_FACET_KEY,
                                            )[0]
                                        }
                                        bopiwFacet={
                                            normalizedGRSFacets?.filter(
                                                (facet: FacetNormalized) =>
                                                    facet.facetKey ===
                                                    GRS_BOPIW_FACET_KEY,
                                            )[0]
                                        }
                                        buyInWarehouseFacet={
                                            normalizedGRSFacets?.filter(
                                                (facet: FacetNormalized) =>
                                                    facet.facetKey ===
                                                    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
                                            )[0]
                                        }
                                        isBopimEligible={
                                            (searchResult as SearchResultGRS)
                                                ?.mdoLocation?.isPickupEligible
                                        }
                                        isBopiwEligible={
                                            (searchResult as SearchResultGRS)
                                                ?.myWarehouse?.isPickupEligible
                                        }
                                        facetConfig={facetConfig}
                                        facet={facet}
                                        grsFacetConfig={grsFacetConfig}
                                        key={facet.facetKey}
                                        locale={locale}
                                        pageType={pageType}
                                        searchService={searchService}
                                    />
                                );

                            // Skipping duplication of these facets as they are rendered within the ProgramEligibilityFilterGroupV2 above in the delivery facet.
                            case GRS_BOPIM_FACET_KEY:
                            case GRS_BUY_IN_WAREHOUSE_FACET_KEY:
                            case GRS_BOPIW_FACET_KEY:
                                return null;

                            default:
                                if (facet.buckets.length > 0) {
                                    return (
                                        <FacetsAccordion
                                            categoryUrlPath={categoryUrlPath}
                                            categoryId={categoryId}
                                            key={facet.facetKey}
                                            facet={facet}
                                            facetConfig={facetConfig}
                                            grsFacetConfig={grsFacetConfig}
                                            locale={locale}
                                            pageType={pageType}
                                            searchService={searchService}
                                            publishedCategories={
                                                publishedCategories
                                            }
                                        />
                                    );
                                }
                                return null;
                        }
                    })}
        </>
    );
}
